/*
 * The deck's two chart shapes, as SVG components: recessive grid, 2px lines,
 * thresholds as dashed guides, colours only from tokens.
 *
 * The mockup drove these imperatively and carried a single floating tooltip
 * element shared by every chart on the page (`TT`). That is replaced here by a
 * native <title> per hit region: it reads out to screen readers, survives a
 * re-render, and needs no portal, positioning maths or global. The trade is
 * the browser's tooltip delay instead of an instant custom one.
 */
import { FC } from 'react';

import { THRESHOLDS } from './data/types';

/* The 100×26 tile sparkline the Ops Deck tiles already draw. */
export const Spark: FC<{ vals: number[]; cls: string }> = ({ vals, cls }) => {
	if (!vals.length) return null;
	const max = Math.max(1, ...vals);
	const min = Math.min(...vals);
	const pts = vals
		.map(
			(v, i) =>
				`${((i / Math.max(1, vals.length - 1)) * 100).toFixed(1)},${(
					24 -
					((v - min) / Math.max(1, max - min)) * 20
				).toFixed(1)}`
		)
		.join(' ');
	return (
		<svg viewBox="0 0 100 26" preserveAspectRatio="none" aria-hidden="true">
			<polyline className={cls} strokeWidth="2" points={pts} />
		</svg>
	);
};

export interface Series {
	name: string;
	vals: number[];
	color: string;
	area?: boolean;
	dash?: boolean;
}

/* One or two lines against a 0–100 score axis, with the review/decline guides. */
export const LineChart: FC<{
	series: Series[];
	width?: number;
	height?: number;
	xlabels?: string[];
	tip?: (i: number) => string;
}> = ({ series, width = 600, height = 150, xlabels, tip }) => {
	const W = width;
	const H = height;
	const L = 30;
	const R = 8;
	const T = 10;
	const B = 22;
	const n = series[0]?.vals.length ?? 0;
	if (!n) return null;
	const max = 100;
	const x = (i: number) => L + (n <= 1 ? 0 : ((W - L - R) * i) / (n - 1));
	const y = (v: number) => T + (H - T - B) * (1 - Math.min(max, v) / max);

	return (
		<svg viewBox={`0 0 ${W} ${H}`} className="chart" preserveAspectRatio="none">
			{[0, 50, 100].map(v => (
				<g key={v}>
					<line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className="grid" />
					<text x={L - 5} y={y(v) + 3} textAnchor="end">
						{v}
					</text>
				</g>
			))}
			{[
				{ v: THRESHOLDS.review, cls: 'rev', lbl: `review ${THRESHOLDS.review}` },
				{ v: THRESHOLDS.decline, cls: 'dec', lbl: `decline ${THRESHOLDS.decline}` },
			].map(t => (
				<g key={t.cls}>
					<line x1={L} x2={W - R} y1={y(t.v)} y2={y(t.v)} className={`thr ${t.cls}`} />
					<text
						x={W - R}
						y={y(t.v) - 3}
						className={`lbl ${t.cls === 'dec' ? 'rd' : 'a'}`}
						textAnchor="end"
					>
						{t.lbl}
					</text>
				</g>
			))}
			{series.map(s => (
				<g key={s.name}>
					{s.area && (
						<path
							d={`M${x(0)},${y(0)} ${s.vals.map((v, i) => `L${x(i)},${y(v)}`).join(' ')} L${x(n - 1)},${y(0)}Z`}
							className="ar"
							style={{ fill: s.color }}
						/>
					)}
					<polyline
						points={s.vals.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
						className="ln"
						style={{ stroke: s.color, strokeDasharray: s.dash ? '4 3' : undefined }}
					/>
				</g>
			))}
			{/* One hit region per point, so every value is reachable by pointer and
			    by keyboard focus rather than only under a moving crosshair. */}
			{(series[0]?.vals ?? []).map((_, i) => (
				<rect
					key={`hit-${x(i)}`}
					x={x(i) - (W - L - R) / Math.max(1, n - 1) / 2}
					y={T}
					width={(W - L - R) / Math.max(1, n - 1)}
					height={H - T - B}
					className="hit"
				>
					<title>
						{`${tip ? tip(i) : i}\n${series.map(s => `${s.name} ${s.vals[i]}`).join('\n')}`}
					</title>
				</rect>
			))}
			{xlabels?.map((l, k) => (
				<text
					key={l || k}
					x={x(k === 0 ? 0 : k === 1 ? Math.floor(n / 2) : n - 1)}
					y={H - 7}
					textAnchor={k === 0 ? 'start' : k === 2 ? 'end' : 'middle'}
				>
					{l}
				</text>
			))}
		</svg>
	);
};
